import React, { useState } from 'react';
import { X, Zap, Check } from 'lucide-react';
import { createCheckoutSession } from '../../services/stripe/checkout';
import { supabase } from '../../services/supabase/client';
import { trackPixelCustomEvent } from '../../utils/metaPixel';

/**
 * Upgrade modal component showing Starter and Pro plans side-by-side
 * @param {boolean} isOpen - Whether the modal is open
 * @param {function} onClose - Callback when modal is closed
 * @param {string} feature - Feature name that triggered the upgrade
 * @param {string} message - Custom message (optional)
 * @param {Object} profile - User profile to check trial status
 * @param {Object} user - Current user
 * @param {function} refreshProfile - Callback to refresh profile after trial start
 * @param {string} upgradeContext - Context: 'features' (show both plans) or 'property_limit' (show only Pro)
 */
export default function UpgradeModal({
  isOpen,
  onClose,
  feature,
  message,
  profile,
  user,
  refreshProfile,
  upgradeContext = 'features' // Default to showing both plans
}) {
  const [isLoading, setIsLoading] = useState(false);
  const [loadingPlan, setLoadingPlan] = useState(null); // 'starter' or 'pro'
  const [starterBillingPeriod, setStarterBillingPeriod] = useState('monthly'); // 'monthly' or 'yearly'
  const [proBillingPeriod, setProBillingPeriod] = useState('monthly'); // 'monthly' or 'yearly'

  if (!isOpen) return null;

  // Decide which plans to show based on context
  // - 'property_limit': User needs more properties -> show only Pro (Starter has same 1-property limit as Free)
  // - 'features': User needs advanced features (periodic, unlimited photos) -> show both Starter and Pro
  const showStarter = upgradeContext !== 'property_limit';

  // Check if user already had trial
  const hasUsedTrial = profile?.trial_started_at !== null && profile?.trial_started_at !== undefined;

  const handleStartTrial = async (plan) => {
    setIsLoading(true);
    setLoadingPlan(plan);

    try {
      // Start trial for selected plan (starter or pro)
      const { error } = await supabase
        .from('profiles')
        .update({
          subscription_plan: plan,
          subscription_status: 'trialing',
          trial_started_at: new Date().toISOString(),
          trial_ends_at: new Date(Date.now() + 14 * 24 * 60 * 60 * 1000).toISOString(),
        })
        .eq('id', user.id);

      if (error) throw error;

      trackPixelCustomEvent('StartTrial');

      // Refresh profile to update UI
      if (refreshProfile) {
        await refreshProfile();
      }

      onClose();
    } catch (error) {
      console.error('Error starting trial:', error);
      alert('Nie udało się rozpocząć trial. Spróbuj ponownie.');
    } finally {
      setIsLoading(false);
      setLoadingPlan(null);
    }
  };

  const handleCheckout = async (plan, billingPeriod = 'monthly') => {
    setIsLoading(true);
    setLoadingPlan(plan);

    try {
      // Redirect to Stripe checkout for selected plan
      await createCheckoutSession(plan, billingPeriod);
    } catch (error) {
      console.error('Error creating checkout:', error);
      alert('Nie udało się utworzyć sesji płatności.');
      setIsLoading(false);
      setLoadingPlan(null);
    }
  };

  return (
    <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center z-50 p-4 animate-fade-in">
      <div className="bg-white rounded-xl max-w-4xl w-full p-6 md:p-8 relative shadow-[0_8px_32px_rgba(0,0,0,0.12),0_2px_8px_rgba(0,0,0,0.08)] max-h-[90vh] overflow-y-auto">
        {/* Loading overlay */}
        {isLoading && (
          <div className="absolute inset-0 bg-white/95 backdrop-blur-sm rounded-xl flex flex-col items-center justify-center z-10">
            <div className="w-12 h-12 border-4 border-violet-200 border-t-violet-600 rounded-full animate-spin mb-4"></div>
            <p className="text-slate-900 font-bold">
              {loadingPlan === 'starter' ? 'Rozpoczynanie trial Starter...' : 'Przekierowywanie do płatności...'}
            </p>
            <p className="text-sm text-slate-500 mt-2">Proszę czekać</p>
          </div>
        )}

        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-slate-400 hover:text-slate-600 transition-colors duration-300 z-20"
        >
          <X className="w-5 h-5" />
        </button>

        {/* Header */}
        <div className="text-center mb-6">
          <div className="w-16 h-16 bg-gradient-to-br from-violet-600 to-violet-700 rounded-xl flex items-center justify-center mx-auto mb-4 shadow-lg shadow-violet-500/30">
            <Zap className="w-8 h-8 text-white" />
          </div>

          <h2 className="text-2xl md:text-3xl font-bold text-slate-900 mb-2 leading-tight">
            Wybierz Plan
          </h2>

          <p className="text-slate-600 leading-relaxed">
            {message || `Funkcja "${feature}" dostępna w płatnych planach.`}
          </p>
        </div>

        {/* Plans Grid */}
        <div className={`grid grid-cols-1 ${showStarter ? 'md:grid-cols-2' : 'md:grid-cols-1 max-w-md mx-auto'} gap-4 mb-6`}>
          {/* PLAN STARTER - Only show if context allows */}
          {showStarter && (
          <div className="border-2 border-violet-200 rounded-xl p-5 hover:border-violet-300 transition-colors duration-300">
            <div className="text-center mb-4">
              <h3 className="text-xl font-bold text-violet-900">Plan Starter</h3>

              {/* Billing Period Toggle */}
              <div className="flex items-center justify-center gap-2 mt-3 mb-3">
                <button
                  onClick={() => setStarterBillingPeriod('monthly')}
                  className={`px-3 py-1 text-xs font-medium rounded-full transition-all ${
                    starterBillingPeriod === 'monthly'
                      ? 'bg-violet-600 text-white'
                      : 'bg-violet-100 text-violet-700 hover:bg-violet-200'
                  }`}
                >
                  Miesięcznie
                </button>
                <button
                  onClick={() => setStarterBillingPeriod('yearly')}
                  className={`px-3 py-1 text-xs font-medium rounded-full transition-all ${
                    starterBillingPeriod === 'yearly'
                      ? 'bg-violet-600 text-white'
                      : 'bg-violet-100 text-violet-700 hover:bg-violet-200'
                  }`}
                >
                  Rocznie (-17%)
                </button>
              </div>

              <div className="mt-1">
                {starterBillingPeriod === 'monthly' ? (
                  <>
                    <span className="text-4xl font-bold text-violet-600">29</span>
                    <span className="text-violet-600 text-lg font-semibold"> PLN</span>
                    <span className="text-slate-500 text-sm">/mc</span>
                  </>
                ) : (
                  <>
                    <span className="text-4xl font-bold text-violet-600">290</span>
                    <span className="text-violet-600 text-lg font-semibold"> PLN</span>
                    <span className="text-slate-500 text-sm">/rok</span>
                    <div className="text-xs text-slate-500 mt-1">(24 PLN/mc)</div>
                  </>
                )}
              </div>
            </div>

            <ul className="space-y-2 mb-5">
              <li className="flex items-start gap-2 text-sm">
                <Check className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
                <span className="text-slate-700"><strong>1 nieruchomość</strong></span>
              </li>
              <li className="flex items-start gap-2 text-sm">
                <Check className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
                <span className="text-slate-700">Nielimitowane inspekcje</span>
              </li>
              <li className="flex items-start gap-2 text-sm">
                <Check className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
                <span className="text-slate-700">Inspekcje okresowe (periodic)</span>
              </li>
              <li className="flex items-start gap-2 text-sm">
                <Check className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
                <span className="text-slate-700">Porównywanie inspekcji</span>
              </li>
              <li className="flex items-start gap-2 text-sm">
                <Check className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
                <span className="text-slate-700">4 zdjęcia na element</span>
              </li>
              <li className="flex items-start gap-2 text-sm">
                <Check className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
                <span className="text-slate-700">Eksport do PDF</span>
              </li>
            </ul>

            {!hasUsedTrial ? (
              <button
                onClick={() => handleStartTrial('starter')}
                disabled={isLoading}
                className="w-full py-3 px-4 bg-gradient-to-br from-violet-600 to-violet-700 text-white rounded-lg font-semibold hover:shadow-lg hover:shadow-violet-500/30 transition-all duration-300 border border-violet-500/20 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Rozpocznij 14-dniowy trial
              </button>
            ) : (
              <button
                onClick={() => handleCheckout('starter', starterBillingPeriod)}
                disabled={isLoading}
                className="w-full py-3 px-4 bg-gradient-to-br from-violet-600 to-violet-700 text-white rounded-lg font-semibold hover:shadow-lg hover:shadow-violet-500/30 transition-all duration-300 border border-violet-500/20 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Kup Plan Starter
              </button>
            )}

            <p className="text-center text-xs text-slate-500 mt-2">
              {starterBillingPeriod === 'yearly' ? '17% taniej niż miesięcznie' : 'Idealne dla 1 nieruchomości'}
            </p>
          </div>
          )}

          {/* PLAN PRO */}
          <div className="border-2 border-indigo-300 rounded-xl p-5 relative bg-gradient-to-br from-indigo-50/30 to-white hover:border-indigo-400 transition-colors duration-300">
            {showStarter && (
            <div className="absolute -top-3 right-4 bg-gradient-to-br from-indigo-600 to-indigo-700 text-white px-3 py-1 rounded-full text-xs font-bold shadow-md">
              Najpopularniejszy
            </div>
            )}

            <div className="text-center mb-4 mt-2">
              <h3 className="text-xl font-bold text-indigo-900">Plan Pro</h3>

              {/* Billing Period Toggle */}
              <div className="flex items-center justify-center gap-2 mt-3 mb-3">
                <button
                  onClick={() => setProBillingPeriod('monthly')}
                  className={`px-3 py-1 text-xs font-medium rounded-full transition-all ${
                    proBillingPeriod === 'monthly'
                      ? 'bg-indigo-600 text-white'
                      : 'bg-indigo-100 text-indigo-700 hover:bg-indigo-200'
                  }`}
                >
                  Miesięcznie
                </button>
                <button
                  onClick={() => setProBillingPeriod('yearly')}
                  className={`px-3 py-1 text-xs font-medium rounded-full transition-all ${
                    proBillingPeriod === 'yearly'
                      ? 'bg-indigo-600 text-white'
                      : 'bg-indigo-100 text-indigo-700 hover:bg-indigo-200'
                  }`}
                >
                  Rocznie (-17%)
                </button>
              </div>

              <div className="mt-1">
                {proBillingPeriod === 'monthly' ? (
                  <>
                    <span className="text-4xl font-bold text-indigo-600">49</span>
                    <span className="text-indigo-600 text-lg font-semibold"> PLN</span>
                    <span className="text-slate-500 text-sm">/mc</span>
                  </>
                ) : (
                  <>
                    <span className="text-4xl font-bold text-indigo-600">490</span>
                    <span className="text-indigo-600 text-lg font-semibold"> PLN</span>
                    <span className="text-slate-500 text-sm">/rok</span>
                    <div className="text-xs text-slate-500 mt-1">(41 PLN/mc)</div>
                  </>
                )}
              </div>
            </div>

            <ul className="space-y-2 mb-5">
              <li className="flex items-start gap-2 text-sm">
                <Check className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
                <span className="text-slate-700"><strong>Do 5 nieruchomości</strong></span>
              </li>
              <li className="flex items-start gap-2 text-sm">
                <Check className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
                <span className="text-slate-700">Nielimitowane inspekcje</span>
              </li>
              <li className="flex items-start gap-2 text-sm">
                <Check className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
                <span className="text-slate-700">Inspekcje okresowe (periodic)</span>
              </li>
              <li className="flex items-start gap-2 text-sm">
                <Check className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
                <span className="text-slate-700">Porównywanie inspekcji</span>
              </li>
              <li className="flex items-start gap-2 text-sm">
                <Check className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
                <span className="text-slate-700">4 zdjęcia na element</span>
              </li>
              <li className="flex items-start gap-2 text-sm">
                <Check className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
                <span className="text-slate-700">Eksport do PDF</span>
              </li>
              <li className="flex items-start gap-2 text-sm">
                <Check className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
                <span className="text-slate-700">Priorytetowe wsparcie</span>
              </li>
            </ul>

            {!hasUsedTrial ? (
              <button
                onClick={() => handleStartTrial('pro')}
                disabled={isLoading}
                className="w-full py-3 px-4 bg-gradient-to-br from-indigo-600 to-indigo-700 text-white rounded-lg font-semibold hover:shadow-lg hover:shadow-indigo-500/30 transition-all duration-300 border border-indigo-500/20 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Rozpocznij 14-dniowy trial
              </button>
            ) : (
              <button
                onClick={() => handleCheckout('pro', proBillingPeriod)}
                disabled={isLoading}
                className="w-full py-3 px-4 bg-gradient-to-br from-indigo-600 to-indigo-700 text-white rounded-lg font-semibold hover:shadow-lg hover:shadow-indigo-500/30 transition-all duration-300 border border-indigo-500/20 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Kup Plan Pro
              </button>
            )}

            <p className="text-center text-xs text-slate-500 mt-2">
              {proBillingPeriod === 'yearly' ? '17% taniej niż miesięcznie' : 'Idealne dla 2-5 nieruchomości'}
            </p>
          </div>
        </div>

        {/* Trial Info */}
        {!hasUsedTrial && (
          <div className="bg-violet-50 border border-violet-200 rounded-lg p-4 mb-4">
            <p className="text-sm text-violet-900 text-center">
              <strong>🎁 14 dni trial za darmo</strong> - wybierz Starter lub Pro. Bez karty kredytowej. Anuluj w dowolnym momencie.
            </p>
          </div>
        )}

        {/* Cancel button */}
        <div className="flex justify-center">
          <button
            onClick={onClose}
            className="px-6 py-2 text-slate-600 hover:text-slate-900 font-medium transition-colors duration-300"
          >
            Może później
          </button>
        </div>
      </div>
    </div>
  );
}
